// --- Dependencies -------------------------------------------------------------------------------
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useDictation from "@/hooks/useDictation";
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TyInteraction, TyTeamMember } from "@/types";
import Icon from "@/UI/atoms/Icon";
import ChoiceInput from "@/UI/inputs/ChoiceInput";
import DateTimeInput from "@/UI/inputs/DateTimeInput";
import SelectInput from "@/UI/inputs/SelectInput";
import TextInput from "@/UI/inputs/TextInput";
import "../../inputs/field.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyInteractionFormDetails = {
    /** The enquiry, when there is one. Absent for a contact that belongs to none. */
    leadId?: string | null;
    /** The guarantee it is about — the after-the-sale half. Never passed alongside a lead. */
    warrantyId?: string | null;
    /** Who it is with. Required when there is no lead to read them off. */
    customerId?: string | null;
    /** The entry being corrected, or `null` to write a new one. */
    interaction?: TyInteraction | null;
    /**
     * Which half to open on, when the caller knows better than the record does. `true` books a
     * follow-up; `false` closes one out, which is what the card's own button does.
     */
    planning?: boolean;
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/** A row written before `occurredAt` became `date` has no date at all, and `Invalid Date` throws. */
const toIso = (value: Date): string => (Number.isNaN(value.getTime()) ? new Date() : value).toISOString();

const shiftDays = (days: number): Date => {
    const when = new Date();
    when.setDate(when.getDate() + days);
    return when;
};

/**
 * One contact — the one that happened, or the one somebody has promised to make. **One form, and
 * the first thing in it is which of the two you are writing.**
 *
 * They are the same record, so they were always one endpoint: a follow-up is an entry with a date
 * in the future and no outcome yet. But they are not the same *act*, and a form that worked that
 * out silently from whatever date you happened to pick is a form that lets somebody mark next
 * Tuesday's call as done today. So the choice is made out loud, at the top, above a rule — and
 * everything below it changes to match:
 *
 * - **Happened**: the date cannot be in the future, it asks for the outcome, which way the contact
 *   went, and what was said.
 * - **Booked**: the date cannot be in the past, and **four fields are gone rather than empty** —
 *   the outcome, because "planned" is the absence of one; the direction, because who rings whom is
 *   not decided until somebody does; the summary, because nothing has been said yet; and the
 *   catalog channel, because a company cannot book an enquiry on its own behalf.
 *
 * The summary takes dictation, because the moment this form is opened is the moment somebody has
 * just put a phone down — and a CRM goes unfilled one untyped sentence at a time.
 *
 * **Who it belongs to is a field.** A manager books next week's viewing for the agent who will make
 * it, and a desk logs the call a colleague took; it defaults to whoever is signed in.
 *
 * What it is *about* is not a field: it is whatever opened the form. An enquiry's screen passes the
 * lead, a warranty card passes the warranty, and a person's own timeline passes neither — which is
 * why the two are never offered as a choice here.
 */
export default function InteractionFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { leadId, warrantyId, customerId, interaction, planning, onDone } = details as TyInteractionFormDetails;

    /**
     * Which half the form opens on. An entry still without an outcome after its date has passed is
     * overdue, not planned — but an explicit `planning` wins over both, because that is how a booked
     * call is closed out: the card's own button says "record what happened" and opens this on the
     * side where that can be written.
     */
    const startsPlanned = planning !== undefined ? planning : interaction ? !interaction.status && new Date(interaction.date) > new Date() : false;

    /**
     * **A contact whose date has passed is a record of something that happened.** The four fields
     * that describe the act — when, which way, what kind, whose — stop being editable, and the four
     * that are only knowable afterwards stay open. The API refuses the same four; this is what makes
     * the refusal visible instead of a surprise after the form is filled in.
     */
    const isFrozen = !!interaction && new Date(interaction.date) <= new Date();

    const [isPlanned, setIsPlanned] = useState(startsPlanned);
    const [channel, setChannel] = useState<string>(interaction?.channel ?? "call");
    const [direction, setDirection] = useState<string>(interaction?.direction ?? "outbound");
    const [category, setCategory] = useState<string>(interaction?.category ?? "");
    /**
     * A booked call being closed out keeps its own date **unless that date is still ahead** — it was
     * made early, so it happened now. Anything already in the past keeps the hour it was booked for,
     * which is the hour it actually happened.
     */
    const opening = interaction ? new Date(interaction.date) : startsPlanned ? shiftDays(1) : new Date();
    const [date, setDate] = useState<string | null>(toIso(!startsPlanned && opening > new Date() ? new Date() : opening));
    /** The outcome, as the one question it really is: did they answer? `failed` is the unticked half. */
    const [isAnswered, setIsAnswered] = useState(interaction ? interaction.status !== "failed" : true);
    const [summary, setSummary] = useState(interaction?.summary ?? "");
    const [notes, setNotes] = useState(interaction?.notes ?? "");
    const [owner, setOwner] = useState<string>(typeof interaction?.client === "object" && interaction.client ? interaction.client._id : "");

    const team = useFetch<{ members: TyTeamMember[] }>("/team");

    const path = interaction ? `/crm/interactions/${interaction._id}` : "/crm/interactions";
    const { loading, error, succeededAt, invoke } = useFetch(path, {}, false);

    // Each finished phrase arrives once and is appended; reading the whole list would re-add it all.
    const dictation = useDictation((phrase) => setSummary((current) => (current ? `${current} ${phrase}` : phrase)));

    useEffect(() => {
        if (!succeededAt) return;
        onClose();
        onDone();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    /** Switching sides moves the date with it, or the form would sit on a date its own half refuses. */
    const switchMode = (planned: boolean) => {
        setIsPlanned(planned);
        const when = date ? new Date(date) : new Date();
        const isWrongSide = planned ? when <= new Date() : when > new Date();
        if (isWrongSide) setDate(toIso(planned ? shiftDays(1) : new Date()));
    };

    const handleSubmit = () => {
        if (dictation.isListening) dictation.stop();

        const data: Record<string, unknown> = {
            channel,
            status: isPlanned ? null : isAnswered ? "completed" : "failed",
            summary: isPlanned ? null : summary.trim() || null,
            notes: notes.trim() || null,
        };

        // The four the API freezes once the date is behind us. Sent only while they can still move.
        if (!isFrozen) {
            data.category = category || null;
            data.date = date ?? undefined;
            // Nothing has happened yet, so this has no honest answer to send.
            data.direction = isPlanned ? "outbound" : direction;
            data.client = owner || null;
        }
        if (!interaction) {
            // One subject, in this order: the API reads the person off whichever is given, and
            // refuses a row that claims both an enquiry and a guarantee.
            if (leadId) data.lead = leadId;
            else if (warrantyId) data.warranty = warrantyId;
            else data.customer = customerId;
        }

        invoke({ method: interaction ? "PATCH" : "POST", data });
    };

    return (
        <section className="side-form side-form--center interaction-form">
            <header className="side-form__header">
                <h2 className="side-form__title">{interaction ? t.crm.editInteraction : isPlanned ? t.crm.bookFollowUp : t.crm.addInteraction}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                {/* The question the rest of the form hangs off, asked first and asked big. */}
                <div className="interaction-form__mode" role="group" aria-label={t.crm.fields.date}>
                    <button
                        type="button"
                        className={`interaction-form__side${isPlanned ? "" : " interaction-form__side--on"}`}
                        onClick={() => switchMode(false)}
                        disabled={loading || isFrozen}
                        aria-pressed={!isPlanned}
                    >
                        <Icon name="check" size={18} />
                        <span>{t.crm.pastOrPlanned}</span>
                    </button>

                    <button
                        type="button"
                        className={`interaction-form__side${isPlanned ? " interaction-form__side--on" : ""}`}
                        onClick={() => switchMode(true)}
                        disabled={loading || isFrozen}
                        aria-pressed={isPlanned}
                    >
                        <Icon name="calendar" size={18} />
                        <span>{t.crm.pastOrPlannedOff}</span>
                    </button>
                </div>

                <hr className="interaction-form__rule" />

                {/* Said once, at the top, rather than as a tooltip on each of the four grey fields. */}
                {isFrozen && <p className="side-form__hint">{t.crm.frozenPast}</p>}

                {/*
                 * How, and which way — one question asked in two halves, so one row. The direction
                 * is second because it only makes sense once the channel is known, and it is gone
                 * entirely on a booked call: who rings whom is not decided until somebody does.
                 * The catalog is something a visitor does to us, so it is never something to book.
                 */}
                <div className="side-form__row side-form__row--pair">
                    <SelectInput
                        value={channel}
                        onChange={setChannel}
                        options={constants.interactionChannels
                            .filter((item) => !isPlanned || item !== "catalog")
                            .map((item) => ({ value: item, label: t.crm.channels[item] }))}
                        label={t.crm.fields.channel}
                        disabled={loading}
                    />

                    {!isPlanned && (
                        <ChoiceInput
                            value={direction}
                            onChange={(next) => setDirection(next || "outbound")}
                            options={constants.interactionDirections.map((item) => ({ value: item, label: t.crm.directions[item] }))}
                            label={t.crm.fields.direction}
                            disabled={loading || isFrozen}
                        />
                    )}
                </div>

                {/*
                 * When, and whether they answered. The outcome is two values and nothing else — it
                 * came off a three-option picker and became the tick it always was — so it fits
                 * beside the date instead of taking a line of its own.
                 *
                 * **Disabled while the call is still booked**, rather than hidden: there is nothing
                 * to report about next Tuesday, and a box that disappears reads as a missing field
                 * where a greyed one reads as a question that is not due yet.
                 *
                 * The half the form is on sets the picker's bound, so it refuses the wrong side
                 * itself rather than the API doing it after the rest has been filled in.
                 */}
                <div className="side-form__row side-form__row--tail">
                    <DateTimeInput
                        value={date}
                        onChange={setDate}
                        label={t.crm.fields.date}
                        min={isPlanned ? new Date() : null}
                        max={isPlanned ? null : new Date()}
                        disabled={loading || isFrozen}
                    />

                    <label
                        className={`side-form__check side-form__check--inline interaction-form__answered${isPlanned ? " interaction-form__answered--off" : ""}`}
                    >
                        <input
                            type="checkbox"
                            checked={!isPlanned && isAnswered}
                            onChange={(event) => setIsAnswered(event.target.checked)}
                            disabled={loading || isPlanned}
                        />
                        <span>{isPlanned ? t.crm.answeredHint : t.crm.answered}</span>
                    </label>
                </div>

                {/* What kind of contact it was, and whose it is. Two short answers, one row. */}
                <div className="side-form__row side-form__row--pair">
                    <SelectInput
                        value={category}
                        onChange={setCategory}
                        options={[
                            { value: "", label: t.crm.notSet },
                            ...constants.interactionCategories.map((item) => ({ value: item, label: t.crm.categories[item] })),
                        ]}
                        label={t.crm.fields.category}
                        disabled={loading || isFrozen}
                    />

                    <SelectInput
                        value={owner}
                        onChange={setOwner}
                        options={[
                            { value: "", label: t.crm.notSet },
                            ...(team.data?.members ?? []).map((member) => ({ value: member._id, label: member.name })),
                        ]}
                        label={t.crm.responsible}
                        disabled={loading || isFrozen}
                    />
                </div>

                {/* Dictated rather than typed: this form is opened with a phone just put down.
                    Absent when booking — there is nothing yet to summarise. */}
                {!isPlanned && (
                    <>
                        <TextField
                            className="field field--area interaction-form__summary"
                            value={summary}
                            onChange={(event) => setSummary(event.target.value)}
                            label={t.crm.fields.summary}
                            disabled={loading}
                            multiline
                            minRows={3}
                            slotProps={{
                                input: dictation.isSupported
                                    ? {
                                          endAdornment: (
                                              <InputAdornment position="end" className="interaction-form__mic-slot">
                                                  <IconButton
                                                      className={`interaction-form__mic${dictation.isListening ? " interaction-form__mic--on" : ""}`}
                                                      onClick={dictation.toggle}
                                                      disabled={loading}
                                                      aria-label={dictation.isListening ? t.crm.voiceStop : t.crm.voiceStart}
                                                      title={dictation.isListening ? t.crm.voiceStop : t.crm.voiceStart}
                                                  >
                                                      <Icon name="mic" size={16} />
                                                  </IconButton>
                                              </InputAdornment>
                                          ),
                                      }
                                    : undefined,
                            }}
                            variant="outlined"
                            fullWidth
                        />

                        {dictation.isListening && <p className="interaction-form__listening">{t.crm.voiceStop}</p>}
                    </>
                )}

                <TextInput value={notes} onChange={setNotes} label={t.crm.fields.notes} multiline rows={2} disabled={loading} />

                {error && <p className="side-form__error">{error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={loading}>
                    {loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
